import { Info, Search } from "lucide-react";
import { getReminderStageLabel } from "@/lib/utils/reminder-stage-label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../../../components/ui/select";
import {
	ToggleGroup,
	ToggleGroupItem,
} from "../../../../components/ui/toggle-group";
import { TooltipWrapper } from "../../../../components/ui/tooltip-wrapper";
import { useAccountingStore } from "../../../../store/Accounting";
import { useRemindersStore } from "../../../../store/Reminders";
import { AccountingRecipientSelector } from "../../components/accounting-recipient-selector.component";
import { MahnwesenFilters, MahnwesenView } from "../payment-reminder.types";
import { MAX_REMINDER_STAGE } from "../utils/reminder-stage";

const VIEW_LABELS: Record<MahnwesenView, string> = {
	due: "Fällig",
	collection: "Inkassofälle",
	paused: "Pausiert",
	history: "Verlauf",
};

const ALL = "ALL";

interface MahnwesenToolbarProps {
	view: MahnwesenView;
	onViewChange: (view: MahnwesenView) => void;
	filters: MahnwesenFilters;
	onFiltersChange: (changes: Partial<MahnwesenFilters>) => void;
	children?: React.ReactNode;
	actions?: React.ReactNode;
}

export const MahnwesenToolbar: React.FC<MahnwesenToolbarProps> = ({
	view,
	onViewChange,
	filters,
	onFiltersChange,
	children,
	actions,
}) => {
	const dueCount = useRemindersStore((state) => state.dueCount);
	const recipientsLookup = useAccountingStore(
		(state) => state.recipientsLookup
	);

	return (
		<div className="mb-4 flex items-center justify-between gap-4 border-b border-gray-200 py-3">
			<div className="flex flex-wrap items-center gap-x-6 gap-y-3">
				<TooltipWrapper title="Der Mahnlauf listet alle offenen Rechnungen, deren Zahlungsfrist und Wartezeit abgelaufen sind. Fristen und Gebühren stellen Sie unter Administration → Mahnungswesen ein, Titel und Brieftexte in der PDF-Gestaltung.">
					<button
						type="button"
						className="p-1 text-gray-400 hover:text-gray-600"
						aria-label="Zweck anzeigen"
					>
						<Info className="h-4 w-4" />
					</button>
				</TooltipWrapper>
				<div className="flex items-center gap-2">
					<span className="whitespace-nowrap text-sm text-gray-500">
						Ansicht:
					</span>
					<ToggleGroup
						type="single"
						value={view}
						onValueChange={(value) =>
							value && onViewChange(value as MahnwesenView)
						}
						aria-label="Ansicht"
						className="rounded-md border border-gray-300 p-0.5"
					>
						{(Object.keys(VIEW_LABELS) as MahnwesenView[]).map(
							(value) => (
								<ToggleGroupItem
									key={value}
									value={value}
									size="sm"
									className="h-8 px-3 text-sm data-[state=on]:bg-stone-200"
									aria-label={VIEW_LABELS[value]}
									data-testid={`reminder-mode-${value}`}
								>
									{VIEW_LABELS[value]}
									{value === "due" &&
										dueCount > 0 &&
										` (${dueCount})`}
								</ToggleGroupItem>
							)
						)}
					</ToggleGroup>
				</div>
				<div className="h-8 w-px bg-gray-200" />
				{children}
				<AccountingRecipientSelector
					selectedRecipientId={filters.recipientId ?? ALL}
					onRecipientChange={(recipientId) =>
						onFiltersChange({
							recipientId:
								recipientId === ALL ? null : recipientId,
						})
					}
					recipientList={recipientsLookup}
				/>
				<div className="flex items-center gap-2">
					<span className="whitespace-nowrap text-sm text-gray-500">
						Mahnstufe:
					</span>
					<Select
						value={
							filters.stage === null ? ALL : String(filters.stage)
						}
						onValueChange={(value) =>
							onFiltersChange({
								stage: value === ALL ? null : Number(value),
							})
						}
					>
						<SelectTrigger
							className="h-10 w-48"
							aria-label="Mahnstufe filtern"
							data-testid="mahnwesen-stage-filter"
						>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value={ALL}>Alle</SelectItem>
							{Array.from(
								{ length: MAX_REMINDER_STAGE },
								(_, index) => (
									<SelectItem
										key={index + 1}
										value={String(index + 1)}
									>
										{getReminderStageLabel(index + 1)}
									</SelectItem>
								)
							)}
						</SelectContent>
					</Select>
				</div>
				<div className="relative">
					<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
					<input
						type="text"
						placeholder="Suchen..."
						value={filters.search}
						onChange={(event) =>
							onFiltersChange({ search: event.target.value })
						}
						className="h-10 w-64 rounded-md border border-gray-300 pl-10 pr-4 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
						aria-label="Rechnung oder Empfänger suchen"
						data-testid="mahnwesen-search-input"
					/>
				</div>
			</div>
			<div className="flex shrink-0 items-center gap-2">{actions}</div>
		</div>
	);
};
